import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';

import { TranslateService } from '../../../services/translate.service';
import { DirectionService } from '../../../services/direction.service';
import { UiSettingsService, NotificationSize, NotificationPosition, FontScale, TableDensity, MotionPref } from '../../../services/ui-settings.service';
import { SettingsSeedService } from '../../../services/settings-seed.service';

type SettingsCategory = 'notifications' | 'display' | 'language' | 'tables' | 'diagnostics';

@Component({
  selector: 'app-settings.component',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule, SelectButtonModule, ToggleSwitchModule, ToastModule, TagModule, TableModule, InputTextModule],
  providers: [MessageService],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss'
})
export class SettingsComponent {
  dialogVisible = true;

  categories: { id: SettingsCategory; icon: string; label: string }[] = [
    { id: 'notifications', icon: 'pi pi-bell', label: 'Notifications' },
    { id: 'display', icon: 'pi pi-desktop', label: 'Display' },
    { id: 'language', icon: 'pi pi-language', label: 'Language' },
    { id: 'tables', icon: 'pi pi-table', label: 'Tables' },
    { id: 'diagnostics', icon: 'pi pi-microchip', label: 'Diagnostics' }
  ];

  activeCategory: SettingsCategory = 'notifications';

  notificationSizeOptions = [
    { label: 'Small', value: 'small' as NotificationSize },
    { label: 'Normal', value: 'normal' as NotificationSize },
    { label: 'Large', value: 'large' as NotificationSize },
    { label: 'Massive', value: 'massive' as NotificationSize }
  ];

  notificationPositionOptions = [
    { label: 'Top Right', value: 'top-right' as NotificationPosition },
    { label: 'Top Center', value: 'top-center' as NotificationPosition },
    { label: 'Top Left', value: 'top-left' as NotificationPosition },
    { label: 'Bottom Right', value: 'bottom-right' as NotificationPosition },
    { label: 'Bottom Center', value: 'bottom-center' as NotificationPosition },
    { label: 'Bottom Left', value: 'bottom-left' as NotificationPosition }
  ];

  fontScaleOptions = [
    { label: 'Small', value: 'small' as FontScale },
    { label: 'Normal', value: 'normal' as FontScale },
    { label: 'Large', value: 'large' as FontScale }
  ];

  tableDensityOptions = [
    { label: 'Compact', value: 'compact' as TableDensity },
    { label: 'Normal', value: 'normal' as TableDensity },
    { label: 'Comfortable', value: 'comfortable' as TableDensity }
  ];

  motionOptions = [
    { label: 'Normal', value: 'normal' as MotionPref },
    { label: 'Reduced', value: 'reduced' as MotionPref },
    { label: 'Off', value: 'off' as MotionPref }
  ];

  languageOptions: { label: string; value: 'en' | 'he' }[] = [
    { label: 'English', value: 'en' },
    { label: 'עברית', value: 'he' }
  ];

  previewTableRows = [
    { name: 'Sample Row A', status: 'Active' },
    { name: 'Sample Row B', status: 'Pending' }
  ];

  currentLang: 'en' | 'he' = (localStorage.getItem('lang') as 'en' | 'he') || 'en';

  // PrimeNG's p-toast doesn't reliably react to a `position` input change on an already-rendered
  // instance, so we destroy and recreate it (via *ngIf) whenever the position setting changes.
  previewToastVisible = true;

  // Toggled by the animation demo button so users can see how the Motion setting affects real transitions.
  motionDemoMoved = false;

  seedInput = '';
  seedApplyError = false;

  constructor(
    private router: Router,
    public uiSettingsService: UiSettingsService,
    public translateServ: TranslateService,
    private directionService: DirectionService,
    private messageService: MessageService,
    private settingsSeedService: SettingsSeedService
  ) {}

  // Recomputed from the signal each access, so it always reflects the latest settings state.
  get currentSeed(): string {
    return this.settingsSeedService.generateSeed();
  }

  get settings() {
    return this.uiSettingsService.settings;
  }

  goBack(): void {
    this.router.navigate(['/']);
  }

  setNotificationSize(value: NotificationSize): void {
    this.uiSettingsService.update('notificationSize', value);
  }

  setNotificationPosition(value: NotificationPosition): void {
    this.uiSettingsService.update('notificationPosition', value);
    this.previewToastVisible = false;
    setTimeout(() => (this.previewToastVisible = true));
  }

  setNotificationBlinking(value: boolean): void {
    this.uiSettingsService.update('notificationBlinking', value);
  }

  setFontScale(value: FontScale): void {
    this.uiSettingsService.update('fontScale', value);
  }

  setTableDensity(value: TableDensity): void {
    this.uiSettingsService.update('tableDensity', value);
  }

  setMotion(value: MotionPref): void {
    this.uiSettingsService.update('motion', value);
  }

  toggleMotionDemo(): void {
    this.motionDemoMoved = !this.motionDemoMoved;
  }

  setCompactSpacing(value: boolean): void {
    this.uiSettingsService.update('compactSpacing', value);
  }

  setHighContrast(value: boolean): void {
    this.uiSettingsService.update('highContrast', value);
  }

  setTableGridlines(value: boolean): void {
    this.uiSettingsService.update('tableGridlines', value);
  }

  // Mirrors app.topbar.ts changeLang() so language switching behaves identically from Settings.
  changeLanguage(lang: 'en' | 'he'): void {
    const toChange = this.directionService.updateLanguage(lang);
    if (toChange) {
      this.currentLang = lang;
      localStorage.setItem('lang', lang);
      this.directionService.setDirection(lang === 'en' ? 'ltr' : 'rtl');
    }
  }

  showTestNotification(): void {
    this.messageService.add({
      key: 'settingsPreview',
      severity: 'info',
      summary: this.translateServ.dictionary['SETTINGS_TEST_NOTIF_TITLE'] || 'Test Notification',
      detail: this.translateServ.dictionary['SETTINGS_TEST_NOTIF_DETAIL'] || 'This is how your notifications will look.',
      life: 4000
    });
  }

  resetToDefaults(): void {
    this.uiSettingsService.reset();
  }

  copySeed(): void {
    navigator.clipboard.writeText(this.currentSeed).then(() => {
      this.messageService.add({
        key: 'settingsPreview',
        severity: 'success',
        summary: this.translateServ.dictionary['SETTINGS_SEED_COPIED_TITLE'] || 'Copied',
        detail: this.translateServ.dictionary['SETTINGS_SEED_COPIED_DETAIL'] || 'Settings seed copied to clipboard.',
        life: 3000
      });
    });
  }

  applySeedInput(): void {
    this.seedApplyError = !this.settingsSeedService.applySeed(this.seedInput);
    if (this.seedApplyError) return;

    this.seedInput = '';
    this.messageService.add({
      key: 'settingsPreview',
      severity: 'success',
      summary: this.translateServ.dictionary['SETTINGS_SEED_APPLIED_TITLE'] || 'Applied',
      detail: this.translateServ.dictionary['SETTINGS_SEED_APPLIED_DETAIL'] || 'Settings seed applied.',
      life: 3000
    });
  }
}